<template>
    <header-index />
    <div class="get-inspired-container">
        <section class="category-banner" 
        style="background-image: url('https://images.leaflypackaging.com/profile/upload/2025/11/24/Get-Inspired_banner_20251124100328A059.webp');">
            <div class="container mx-auto px-4 h-[100%] flex flex-col justify-center">
                <div class="pb-2 content-center">
                    <h1 class="leafly-page-title">Get Inspired</h1>
                    <p class="leafly-page-second-title max-w-[680px]">Discover creative packaging solutions and innovative ideas to elevate your brand. Browse our collection of inspiration for your next packaging project.</p>
                </div>
               
            </div>
        </section>

        <!-- Categories Section -->
        <div class="bg-gray-50 py-12">
            <div class="container mx-auto px-4">
               
                <!-- Inspiration Grid -->
                <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    <div v-for="article in articlesPage.rows" class="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow">
                        <div class="relative h-53">
                            <img :src="article.imageUrl" :alt="article.imageAlt" class="w-full h-[220px] object-fill" loading="lazy">
                        </div>
                        <div class="p-4">
                            <h2 class="text-lg font-weight-600 mb-2 line-clamp-2 leading-2 multi-line-ellipsis h-[52px]">
                                <nuxt-link :to="article.fullUrl" class="leafly-article-item-title text-base  line-clamp-2  font-size-22px hover:text-green-700">
                                    {{ article.title }}
                                </nuxt-link> 
                            </h2>
                            <p class="mt-2  text-gray-600 mb-4 line-clamp-3 multi-line-ellipsis">{{ article.description }}</p>
                            <div class="flex justify-between items-center pt-4">
                                <span class="text-xs text-gray-500">{{ article.createTime?.slice(0, 10) }}</span>
                                <nuxt-link :to="article.fullUrl" class="text-green-600 text-sm font-weight-400 hover:underline">Read More</nuxt-link>
                            </div>
                        </div>
                    </div>
                </div>
                
                 <!-- 产品结束提示 -->
                 <div class="flex justify-center mt-12">
                    <div class="flex items-center space-x-1">
                        <page-icon :page-num="props.pageNum" :page-size="props.pageSize" :total="articlesPage.total!" />
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Newsletter Section -->
        <div class="bg-green-600 py-16">
            <div class="container mx-auto px-4 text-center">
                <h2 class="text-3xl font-weight-600 text-white mb-4">Stay Inspired</h2>
                <p class="text-white mb-8 max-w-2xl mx-auto">Subscribe to our newsletter for the latest packaging inspiration, industry trends, and exclusive offers.</p>
                <div class="max-w-md mx-auto">
                    <div class="flex flex-col sm:flex-row gap-4">
                        <input type="email" placeholder="Your email address" class="flex-grow px-4 py-3 rounded-md focus:outline-none">
                        <button class="bg-white text-green-600 font-weight-600 px-6 py-3 rounded-md hover:bg-gray-100 transition-colors">Subscribe</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
    
    <footer-index />
</template>

<script setup lang="ts">
    import { ref, onMounted, watch } from 'vue'
    import type { Category, NavItemVo,Tags,Page,Article } from '~/types';
    import cmsUtils from '~/utils/cmsUtils';
    import headerIndex from '../../components/header/header-index.vue'
    import footerIndex from '../../components/footer/footer-index.vue'
    import pageIcon from '../../components/common/pageIcon.vue';
    import cmsConfig from '~/cmsConfig';

    // 接收分类数据
     const props = withDefaults(defineProps<{
        category: Category | null
        tagsList?: Tags[]
        pageSize?: number
        pageNum?: number
    }>(), {
        tagsList: () => [],
        pageSize: 12,
        pageNum: 1
    })

    const cmsInfo = await cmsUtils.getCmsInfo()
    const route = useRoute()
    const childCategoryIds = cmsUtils.getChildCategoryIds(props.category!.id, cmsInfo);
    const allCategoryIds = [props.category!.id, ...childCategoryIds];

    // 文章列表数据
    const articlesPage = ref<Page<Article>>({ rows: [], total: 0, pageNum: 1, pageSize: 12 })

    // 获取文章列表的函数
    const fetchArticles = async (pageNum: number) => {
        const body = {
            "websiteId": cmsInfo!.website.id,
            "categoryIds": allCategoryIds,
            "tagsIds": props.tagsList.map(item => item.id),
            "orderType":"top",
            "pageNum": pageNum,
            "pageSize": props.pageSize
        }

        const response = await useHttp("/nuxt/article/list", {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: body
        }) as Page<Article>;
        
        response.rows.forEach((article: Article) => {
            article.fullUrl = cmsUtils.appendCategoryUrl(props.category!, cmsInfo, "/" + article.titleUrl);
        });

        articlesPage.value = response
    }

    // 初始加载
    await fetchArticles(props.pageNum)

    // 监听 pageNum 变化，重新获取数据
    watch(() => props.pageNum, async (newPageNum) => {
        await fetchArticles(newPageNum)
    })

    // JSON-LD 结构化数据（文章列表）
    const baseUrl = (cmsInfo.website.https ? 'https://' : 'http://') + cmsInfo.website.domain
    const canonicalUrl = baseUrl + route.path.replace('.html','')
    useHead({
        script: [
            {
                type: 'application/ld+json',
                innerHTML: JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'ItemList',
                    itemListElement: articlesPage.value.rows.map((article, index) => ({
                        '@type': 'ListItem',
                        position: (props.pageNum - 1) * props.pageSize + index + 1,
                        item: {
                            '@type': 'Article',
                            headline: article.title,
                            ...(article.description ? { description: article.description } : {}),
                            ...(article.imageUrl ? { image: article.imageUrl } : {}),
                            ...(article.updateTime ? { datePublished: article.updateTime } : {}),
                            url: baseUrl + article.fullUrl
                        }
                    })),
                    numberOfItems: articlesPage.value.total,
                    name: props.category?.title || props.category?.name,
                    ...(props.category?.description ? { description: props.category.description } : {}),
                    url: canonicalUrl
                })
            }
        ]
    })

</script>

<style scoped lang='scss'>


.get-inspired-container {
    .hero-section {
        background-size: cover;
        background-position: center;
        padding: 100px 0;
        position: relative;
        
        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(76, 105, 159, 0.8);
            z-index: 0;
        }
        
        .container {
            position: relative;
            z-index: 1;
        }
    }
    
    .navActive {
        border: 2px solid #16a34a;
        color: #16a34a;
    }
}
.multi-line-ellipsis {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis; /* 某些浏览器可能需要这个属性 */
  word-break: break-word; /* 处理长单词或连续字符 */
}
</style>
